import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/seo/Seo';
import { breadcrumbSchema } from '../components/seo/jsonld';

const TermsPage = () => (
    <>
        <Seo
            title="Terms of Service"
            description="Terms of Service for pdfconvertor.shop — acceptable use, intellectual property, limitations of liability, and governing law."
            path="/terms"
            jsonLd={breadcrumbSchema([
                { name: 'Home', path: '/' },
                { name: 'Terms of Service', path: '/terms' },
            ])}
        />

        <div className="page-container narrow">
            <article className="legal-page">
                <header className="page-header">
                    <h1>Terms of Service</h1>
                    <p className="meta">Last updated: April 21, 2026</p>
                </header>

                <p>
                    These Terms of Service (&quot;Terms&quot;) govern your use of the
                    pdfconvertor.shop website and its tools (together, the &quot;Service&quot;).
                    By accessing or using the Service, you agree to be bound by these Terms. If
                    you do not agree, you may not use the Service.
                </p>

                <h2>1. Description of the Service</h2>
                <p>
                    pdfconvertor.shop provides free, browser-based tools for converting and
                    manipulating documents, including Word-to-PDF, Image-to-PDF, PDF
                    compression, PDF-to-Image, and PDF-to-Word conversion. All conversions are
                    performed locally within your browser; no files are transmitted to our
                    servers as part of the conversion process.
                </p>

                <h2>2. Eligibility</h2>
                <p>
                    You must be at least 13 years old to use the Service. If you are under the
                    age of majority in your jurisdiction, you may use the Service only with the
                    involvement and consent of a parent or legal guardian.
                </p>

                <h2>3. Acceptable use</h2>
                <p>You agree not to use the Service to:</p>
                <ul>
                    <li>Process content that is unlawful, infringing, defamatory, obscene, or otherwise objectionable.</li>
                    <li>Violate any applicable law or regulation, including export and privacy laws.</li>
                    <li>
                        Attempt to probe, scan, or test the vulnerability of the Service, or
                        breach any security or authentication measures.
                    </li>
                    <li>
                        Interfere with or disrupt the Service, the servers or networks used to
                        make the Service available, or other users.
                    </li>
                    <li>
                        Reverse engineer or attempt to extract source code of the Service except
                        to the extent permitted by law.
                    </li>
                    <li>
                        Use automated means (bots, scrapers) to access the Service in a way that
                        places unreasonable load on our infrastructure.
                    </li>
                    <li>Remove, obscure, or alter any ads, notices, or attributions.</li>
                </ul>

                <h2>4. Your content</h2>
                <p>
                    You retain all rights to the files you process using the Service. Because
                    conversions happen locally in your browser, we do not receive or store your
                    files, and we do not claim any rights over them. You are solely responsible
                    for the files you process and for ensuring you have the right to do so.
                </p>

                <h2>5. Intellectual property</h2>
                <p>
                    The Service — including its code, design, branding, copy, and aggregate
                    content — is owned by pdfconvertor.shop and is protected by applicable
                    intellectual property laws. We grant you a limited, non-exclusive,
                    non-transferable, revocable license to use the Service for its intended
                    purpose, subject to these Terms.
                </p>
                <p>
                    The Service incorporates open-source libraries, each licensed under its own
                    terms. Notably: <code>pdf-lib</code> (MIT), <code>pdf.js</code> (Apache 2.0),
                    <code>mammoth</code> (BSD), <code>jsPDF</code> (MIT), <code>jszip</code>{' '}
                    (MIT), <code>docx</code> (MIT). Their licenses and attributions are honored.
                </p>

                <h2>6. Advertising</h2>
                <p>
                    The Service is supported by advertisements, primarily through Google AdSense.
                    By using the Service, you acknowledge that ads will be displayed and that
                    advertising partners may use cookies and similar technologies as described
                    in our <Link to="/privacy-policy">Privacy Policy</Link>.
                </p>

                <h2>7. Disclaimers</h2>
                <p>
                    THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;
                    WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. WE
                    DISCLAIM ALL WARRANTIES INCLUDING BUT NOT LIMITED TO WARRANTIES OF
                    MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
                    NON-INFRINGEMENT.
                </p>
                <p>
                    We do not warrant that the Service will meet your requirements, be
                    uninterrupted or error-free, or that conversion output will be accurate,
                    complete, or suitable for any particular purpose. PDF-to-Word output in
                    particular is an approximation and may require manual cleanup.
                </p>

                <h2>8. Limitation of liability</h2>
                <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
                    pdfconvertor.shop OR ITS OPERATORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                    SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR
                    REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
                    GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING FROM (a) YOUR USE OF OR
                    INABILITY TO USE THE SERVICE; (b) ANY CONVERSION ERRORS OR DATA LOSS; OR (c)
                    ANY CONTENT OBTAINED FROM THE SERVICE.
                </p>
                <p>
                    Because the Service is free and processes your files locally, you are
                    responsible for keeping backups of your original documents. We cannot recover
                    a file we never received.
                </p>

                <h2>9. Indemnification</h2>
                <p>
                    You agree to indemnify and hold harmless pdfconvertor.shop, its operators,
                    and their respective affiliates from any claim or demand — including
                    reasonable attorneys' fees — made by any third party due to or arising out of
                    your violation of these Terms, your misuse of the Service, or your violation
                    of any rights of another.
                </p>

                <h2>10. Third-party links</h2>
                <p>
                    The Service may contain links to third-party websites (such as documentation
                    from library authors, or Google's ad-settings pages). We are not responsible
                    for the content, privacy practices, or accuracy of any third-party sites.
                </p>

                <h2>11. Modifications to the Service or Terms</h2>
                <p>
                    We reserve the right to modify or discontinue the Service, temporarily or
                    permanently, with or without notice. We may also revise these Terms from
                    time to time. When we do, we will update the &quot;Last updated&quot; date.
                    Continued use of the Service after changes constitutes acceptance of the
                    revised Terms.
                </p>

                <h2>12. Governing law</h2>
                <p>
                    These Terms are governed by the laws of India, without regard to its
                    conflict-of-laws principles. The courts of Hyderabad, Telangana, India shall
                    have exclusive jurisdiction over any disputes arising from or in connection
                    with these Terms, except where prohibited by applicable consumer-protection
                    law.
                </p>

                <h2>13. Severability</h2>
                <p>
                    If any provision of these Terms is found unenforceable, the remaining
                    provisions will remain in full force and effect.
                </p>

                <h2>14. Contact</h2>
                <p>
                    Questions about these Terms? Email{' '}
                    <a href="mailto:pdf2convertor@gmail.com">pdf2convertor@gmail.com</a> or use
                    the <Link to="/contact">contact form</Link>.
                </p>
            </article>
        </div>
    </>
);

export default TermsPage;
